import type * as React from 'react'

import { cn } from '../../lib/utils'

/**
 * DESIGN.md §5 "Inputs and fields": 36px, a 1px `--input` boundary on field
 * paper, 4px corners, 10px inline padding. Hover darkens the boundary to ink;
 * focus is the shared 3px ink ring at a 2px offset. An invalid field takes the
 * `--danger` boundary and `aria-invalid`, and keeps the ordinary ring.
 * Disabled is recoloured (`--muted`, `--muted-foreground`, `--border`), never
 * faded.
 *
 * `readOnlyDisplay` is the read-only computed value: never a boxed field, a
 * value over a bottom hairline only, so it sits on the form grid without
 * inviting an edit that will never land.
 *
 * `prefix` and `suffix` make a composite field: the value and its unit or
 * currency share one bordered box and one focus ring, with one tab stop.
 *
 * `numeric` sets figures at the end of the field in both languages. Tabular
 * figures are inherited from the body (The Global Figures Rule); `tabular-nums`
 * here only restates them for a field whose font a caller may change. A date
 * is tabular too, but read from its start in both writing directions.
 *
 * `type="search"` and `role="combobox"` size to their content within the
 * available width. `fullWidth` keeps a combobox at its container's width, for
 * a picker that fills a table cell.
 */
function Input({
  className,
  type = 'text',
  invalid,
  readOnlyDisplay,
  numeric,
  prefix,
  suffix,
  readOnly,
  style,
  fullWidth,
  ...props
}: React.ComponentProps<'input'> & {
  invalid?: boolean
  fullWidth?: boolean
  readOnlyDisplay?: boolean
  numeric?: boolean
  prefix?: React.ReactNode
  suffix?: React.ReactNode
}) {
  const sizeToContent = !fullWidth && (type === 'search' || props.role === 'combobox')
  const affixed = Boolean(prefix || suffix)
  const ariaInvalid = props['aria-invalid']
  const isInvalid = Boolean(invalid) || ariaInvalid === true || ariaInvalid === 'true'

  const field = (
    <input
      data-slot="input"
      type={type}
      readOnly={readOnly ?? readOnlyDisplay}
      aria-invalid={invalid || undefined}
      className={cn(
        'h-control w-full min-w-0 rounded-sm border border-input bg-input-background px-2.5 text-body text-foreground',
        'transition-ui placeholder:text-muted-foreground',
        'enabled:hover:border-foreground',
        'focus-visible:outline-3 focus-visible:outline-offset-2 focus-visible:outline-ring',
        'aria-invalid:border-danger aria-invalid:enabled:hover:border-danger',
        'disabled:cursor-not-allowed disabled:border-border disabled:bg-muted disabled:text-muted-foreground',
        sizeToContent && 'field-sizing-content',
        numeric && 'text-end tabular-nums',
        type === 'date' && 'tabular-nums',
        // The whole field opens the picker, so hovering anywhere on it lights
        // the selector button as well as the boundary.
        type === 'file' && [
          'cursor-pointer ps-1 py-0.75',
          'file:me-3 file:h-7 file:cursor-pointer file:rounded-sm file:border file:border-input file:bg-card file:px-3',
          'file:font-medium file:text-foreground file:text-label file:transition-ui',
          'enabled:hover:file:bg-accent enabled:active:file:translate-y-px',
          'disabled:cursor-not-allowed disabled:file:cursor-not-allowed',
          'disabled:file:border-border disabled:file:bg-muted disabled:file:text-muted-foreground',
        ],
        readOnlyDisplay && [
          'rounded-none border-transparent border-b-line-strong bg-transparent px-0.5',
          'enabled:hover:border-transparent enabled:hover:border-b-line-strong',
        ],
        // The group draws the box, the hover and the ring for an affixed field.
        affixed &&
          'h-full rounded-none border-0 bg-transparent focus-visible:outline-none disabled:bg-transparent',
        className,
      )}
      style={
        sizeToContent
          ? { ...style, width: 'auto', minWidth: 'min(16rem, 100%)', maxWidth: '100%' }
          : style
      }
      {...props}
    />
  )

  if (!affixed) {
    return field
  }

  return (
    <div
      data-slot="input-group"
      data-invalid={isInvalid || undefined}
      className={cn(
        // `overflow-hidden` rounds the affix panels with the group. It cannot
        // clip the ring: an outline belongs to the element that draws it.
        'flex h-control min-w-0 items-stretch overflow-hidden rounded-sm border border-input bg-input-background transition-ui',
        'hover:border-foreground',
        'has-[input:focus-visible]:outline-3 has-[input:focus-visible]:outline-offset-2 has-[input:focus-visible]:outline-ring has-[input:focus-visible]:outline-solid',
        'data-[invalid=true]:border-danger data-[invalid=true]:hover:border-danger',
        'has-[input:disabled]:border-border has-[input:disabled]:bg-muted has-[input:disabled]:text-muted-foreground has-[input:disabled]:hover:border-border',
      )}
    >
      {prefix ? (
        <span
          data-slot="input-prefix"
          className="flex min-w-11 shrink-0 items-center justify-center border-e border-border bg-muted px-2 text-identifier text-muted-foreground"
        >
          {prefix}
        </span>
      ) : null}
      {field}
      {suffix ? (
        <span
          data-slot="input-suffix"
          className="flex shrink-0 items-center border-s border-border bg-muted px-2.5 text-identifier text-muted-foreground"
        >
          {suffix}
        </span>
      ) : null}
    </div>
  )
}

export { Input }
